import { message, Pagination, TableColumnsType, Tabs, Typography } from "antd";
import { ContentWrapper, DataTable } from "~/Components";
// import "../../../index.css";
import { Action, TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { drilldownData } from "@utils/drilldownData";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDropdown from "~/Modules/ECMS/MAGP/StatusInformation/ActionDropdown";
import { get, put } from "~/Services";
import { getCurrentModule } from "~/Utils/StringConstants";
import { renderActionsColumn, renderDateColumn, renderUpdatedAtColumn } from "~/Utils/TableRenderFunction";
import { formatSubModule, unformatSubModule } from "./CentralizedStatusInformation";
import { addSasToImages, appendSasToken } from "./Imagehelper";

export const CentralizedApprovalManagement = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchString, setSearchString] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>("createdAt");

  const [approvalId, setApprovalId] = useState<string | undefined>(selectedRowData?.approvalId);

  // single global actionType
  const [actionType, setActionType] = useState<string>();

  const currentModule = getCurrentModule();

  useEffect(() => {
    setApprovalId(selectedRowData?.approvalId);
  }, [selectedRowData]);

  useEffect(() => {
    if (!query.get("activeTab")) {
      navigate(`/centralized/approval-management?activeTab=pending`, {
        replace: true,
      });
    }
  }, []);

  const {
    data: drilldown,
    isLoading: drilldownLoading,
    refetch: refetchDocument,
  } = useDocumentDetails(approvalId || "");

  function capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  useEffect(() => {
    if (approvalId) {
      refetchDocument();
    }
  }, [approvalId]);

  const renderSubModule = (text: string) => capitalizeFirstLetter(text);

  const columns: TableColumnsType = [
    {
      title: "Request Type",
      dataIndex: "action",
      key: "action",
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "module"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "module"
            ? "descend"
            : undefined,
    },
    {
      title: "Sub Module",
      dataIndex: "subModule",
      key: "subModule",
      render: (text: string) => formatSubModule(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "subModule"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "subModule"
            ? "descend"
            : undefined,
    },
    {
      title: "Requested At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Requested By",
      dataIndex: "createdBy",
      key: "createdBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Approved At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Rejected At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Approved By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedBy"
            ? "descend"
            : undefined,
      render: (text: string) => renderUpdatedAtColumn(text),
    },
    {
      title: "Rejected By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      render: (text: string) => renderUpdatedAtColumn(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Rejected Reason",
      dataIndex: "rejectReason",
      key: "rejectReason",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "rejectReason"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "rejectReason"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: any) => {
        const name = "approvalManagement";
        return query.get("activeTab") === "pending"
          ? renderActionsColumn(record, undefined, undefined, undefined, undefined, undefined, name)
          : renderActionsColumn(record);
      },
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    setCurrentPage(0);
    getRecords(order, column.dataIndex, searchString, currentPage);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    const unformattedValue = unformatSubModule(value);
    setSearchString(unformattedValue);
    setIsLoading(isLoading);
  }, 1000);

  useEffect(() => {
    getRecords("desc", "createdAt", searchString, currentPage);
  }, [query?.get("activeTab"), actionType]);

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, 0);
  }, [searchString]);

  const getColumnsByTab = (activeTab: string) => {
    const filteredColumns = columns.filter((column) => {
      if (typeof column.title === "string") {
        switch (activeTab) {
          case "pending":
            return ["Request Type", "Module", "Sub Module", "Requested At", "Requested By", "Actions"].includes(
              column.title
            );
          case "approved":
            return [
              "Request Type",
              "Module",
              "Sub Module",
              "Approved At",
              "Approved By",
              "Requested By",
              "Requested At",
              "Actions",
            ].includes(column.title);
          case "rejected":
            return [
              "Request Type",
              "Module",
              "Sub Module",
              "Rejected At",
              "Rejected By",
              "Requested By",
              "Requested At",
              "Rejected Reason",
              "Actions",
            ].includes(column.title);
          default:
            return true;
        }
      }
      return false;
    });

    return filteredColumns.map((col: any) => ({
      ...col,
      onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
    }));
  };

  const handleApprove = async () => {
    try {
      const formdata = new FormData();
      formdata.append("approvalId", selectedRowData.approvalId);
      formdata.append("status", "APPROVED");
      put("management/update-status", formdata)
        .then((res: any) => {
          message.success("Approved successfully!");
          getRecords(sortOrder, orderByName, searchString, currentPage);
          setDrawerVisible(false);
        })
        .catch(() => {
          message.error("Failed to approve!");
        });
    } catch (error: any) {
      message.error("Failed to approve!");
    }
  };

  const handleReject = async (reason?: string) => {
    if (!reason) return message.error("Please provide reason for rejection");
    try {
      const formdata = new FormData();
      formdata.append("approvalId", selectedRowData.approvalId);
      formdata.append("status", "REJECTED");
      formdata.append("rejectReason", reason);
      put("management/update-status", formdata)
        .then(() => {
          message.success("Rejected successfully!");
          getRecords(sortOrder, orderByName, searchString, currentPage);
          setDrawerVisible(false);
        })
        .catch(() => {
          message.error("Failed to reject!");
        });
    } catch (error) {
      message.error("Failed to reject!");
    }
  };

  const actions: Action[] = [
    { label: "Approve", onClick: handleApprove, type: "primary" },
    {
      label: "Reject",
      onClick: handleReject,
      type: "ghost",
    },
  ];

  const getTabContent = (tabKey: string) => (
    <>
      <DataTable
        tabOffset={60}
        columns={getColumnsByTab(tabKey)}
        className="table-clickable"
        tableData={data ? data.map((d: any) => ({ ...d })) : []}
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        multiSelect={false}
        hideExport={true}
        isServerSearchApi={true}
        searchPlaceholderText={"Search Module / Sub Module"}
        onSearchChange={handleSearchChange}
        onClick={(record) => {
          setSelectedRowData(record);
          setDrawerVisible(true);
        }}
        additionalCTA={
          <ActionDropdown
            value={actionType}
            onChange={setActionType}
          />
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          onChange={(curPage) => {
            let page = curPage - 1;
            setCurrentPage(page);
            getRecords(sortOrder, orderByName, searchString, page);
          }}
          showSizeChanger={false}
        />
      </div>
    </>
  );

  const items = [
    { label: "Pending", key: "pending", children: getTabContent("pending") },
    { label: "Rejected", key: "rejected", children: getTabContent("rejected") },
    { label: "Approved", key: "approved", children: getTabContent("approved") },
  ];

  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    let activeTab = query?.get("activeTab");
    if (!activeTab) return;
    let currentModule = getCurrentModule();
    setIsLoading(true);
    let url = `management/get-documents?page=${currentPage}&size=${10}&application=CENTRALIZED&status=${activeTab}&order=${sortOrder}&orderBy=${orderByParam}&searchText=${searchString}${
      actionType ? `&action=${actionType}` : ""
    }`;
    get(url)
      .then((res: any) => {
        if (res && res.response.data) {
          let envelopes = res.response.data.records;
          setTotalRecords(res.response.data.total);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };

  const imageKeys = [
    "banner_image",
    "square_image",
    "image",
    "web_image",
    "tab_image",
    "video",
    "mobile_image",
    "mobile_banner_image",
    "logo",
    "pdf_url",
    "type_banner_image",
  ];
  const descriptionKeys = [
    "description",
    "description_malay",
    "description_mandarin",
    "type_description_en",
    "type_description_ms",
    "type_description_zh",
    "description_english_tab_1",
    "description_malay_tab_1",
    "description_mandarin_tab_1",
    "description_english_tab_2",
    "description_malay_tab_2",
    "description_mandarin_tab_2",
    "answer_en",
    "answer_malay",
    "answer_mandarian",
    "description_ms",
    "description_zh",
    "image_description",
    "image_description_malay",
    "image_description_mandarin",
    "description_english_1",
    "description_mandarin_1",
    "description_malay_1",
    "terms_condition",
    "terms_condition_malay",
    "terms_condition_mandarin",
    "long_description",
    "long_description_malay",
    "long_description_mandarin",
    "short_description",
    "short_description_malay",
    "short_description_mandarin",
    "description_english_2",
    "description_malay_2",
    "description_mandarin_2",
    "description_english_3",
    "description_malay_3",
    "description_mandarin_3",
    "description_english_4",
    "description_malay_4",
    "description_mandarin_4",
    "description_english_5",
    "description_malay_5",
    "description_mandarin_5",
    "body",
  ];

  const drilldownAny = drilldown?.payload as any;

  const dataWithSas = {
    ...drilldown?.payload,

    ...Object.fromEntries(imageKeys.map((key) => [key, drilldownAny?.[key] ? appendSasToken(drilldownAny[key]) : ""])),

    ...Object.fromEntries(
      descriptionKeys.map((key) => [key, drilldownAny?.[key] ? addSasToImages(drilldownAny[key]) : ""])
    ),
  };

  return (
    <ContentWrapper title={"Approval Management"}>
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`);
          setSearchString("");
          setCurrentPage(0);
          setActionType(undefined); // ✅ reset dropdown filter
        }}
      />
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[selectedRowData?.subModule?.split("_").pop() as keyof typeof drilldownData]}
        actions={query?.get("activeTab") == "pending" ? actions : []}
        data={dataWithSas}
        isLoading={drilldownLoading}
        reviewNotes={
          query.get("activeTab") !== "approved" && query.get("activeTab") !== "rejected"
            ? drilldown?.reviewNotes
            : undefined
        }
        copyClipboard={true}
      />
    </ContentWrapper>
  );
};
